import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users2, Megaphone, UserRound, CornerDownLeft, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { initials } from "@/lib/format";
import { STAGE_STYLES } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { logSearch } from "@/lib/audit";

// Phase 15 — premium command-palette global search anchored to the top bar.
// Searches leads (role-scoped), campaigns & team; keyboard navigable; ⌘/Ctrl+K.
export default function GlobalSearch() {
  const navigate = useNavigate();
  const { options } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState({ leads: [], campaigns: [], team: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const debounceRef = useRef();

  // Flatten results into a single ordered list for keyboard navigation.
  const items = useMemo(() => {
    const list = [];
    res.leads.forEach((l) => list.push({ kind: "lead", id: l.id, data: l }));
    res.campaigns.forEach((c) => list.push({ kind: "campaign", id: c.name, data: c }));
    res.team.forEach((t) => list.push({ kind: "team", id: t.id, data: t }));
    if (q.trim().length >= 2) list.push({ kind: "all", id: "__all__" });
    return list;
  }, [res, q]);

  const go = useCallback((it) => {
    if (!it) return;
    if (q.trim().length >= 2) logSearch(q.trim());
    setOpen(false);
    setQ("");
    if (it.kind === "lead") navigate(`/leads?open=${it.data.id}`);
    else if (it.kind === "campaign") navigate(`/leads?campaign_name=${encodeURIComponent(it.data.name)}`);
    else if (it.kind === "team") navigate("/team");
    else if (it.kind === "all") navigate(`/leads?search=${encodeURIComponent(q.trim())}`);
  }, [navigate, q]);

  // Debounced fetch.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) { setRes({ leads: [], campaigns: [], team: [] }); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term } });
        setRes({ leads: data.data.leads, campaigns: data.data.campaigns, team: data.data.team });
        setActive(0);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // ⌘/Ctrl+K focus + close on outside click.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); inputRef.current?.focus(); setOpen(true);
      }
    };
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(items[active]); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  const hasResults = res.leads.length || res.campaigns.length || res.team.length;
  const showDrop = open && q.trim().length >= 1;
  let idx = -1; // running index across groups to match `active`

  const Row = ({ it, children }) => {
    idx += 1;
    const i = idx;
    return (
      <button
        type="button"
        data-testid={`gs-item-${it.kind}-${it.id}`}
        onMouseEnter={() => setActive(i)}
        onClick={() => go(it)}
        className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors ${active === i ? "bg-loop/[0.07]" : "hover:bg-mist"}`}
      >
        {children}
      </button>
    );
  };

  return (
    <div className="relative hidden flex-1 max-w-md sm:block" ref={boxRef}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
      <input
        ref={inputRef}
        data-testid="global-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search leads, campaigns, team…"
        className="h-10 w-full rounded-xl border border-sky bg-mist/60 pl-9 pr-16 text-sm text-ink outline-none transition-all focus:border-loop focus:bg-white focus:ring-2 focus:ring-loop/20"
      />
      {q ? (
        <button onClick={() => { setQ(""); inputRef.current?.focus(); }} data-testid="gs-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-ink/40 hover:bg-mist hover:text-ink">
          <X className="h-3.5 w-3.5" />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded-md border border-sky bg-white px-1.5 py-0.5 font-sans text-[10px] font-semibold text-ink/40 md:flex">⌘K</kbd>
      )}

      {showDrop && (
        <div data-testid="gs-dropdown" className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border border-sky/70 bg-white shadow-2xl shadow-navy/10 animate-[notifIn_0.16s_ease-out]">
          <div className="max-h-[440px] overflow-y-auto p-2">
            {loading && !hasResults ? (
              <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-ink/50"><Loader2 className="h-4 w-4 animate-spin" />Searching…</div>
            ) : q.trim().length < 2 ? (
              <p className="px-3 py-6 text-center text-xs text-ink/45">Type at least 2 characters to search</p>
            ) : (
              <>
                {res.leads.length > 0 && (
                  <div className="mb-1">
                    <p className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink/40">Leads</p>
                    {res.leads.map((l) => (
                      <Row key={l.id} it={{ kind: "lead", id: l.id, data: l }}>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-navy text-[11px] font-bold text-white"><Users2 className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-medium text-navy">{l.name}</p>
                            {l.stage && <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${STAGE_STYLES[l.stage] || "border-sky text-ink/60"}`}>{options?.stage_meta?.[l.stage]?.name || l.stage}</span>}
                          </div>
                          <p className="truncate text-[11px] text-ink/50">{[l.business_name, l.phone, l.owner_name].filter(Boolean).join(" · ") || "—"}</p>
                        </div>
                      </Row>
                    ))}
                  </div>
                )}
                {res.campaigns.length > 0 && (
                  <div className="mb-1">
                    <p className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink/40">Campaigns</p>
                    {res.campaigns.map((c) => (
                      <Row key={c.name} it={{ kind: "campaign", id: c.name, data: c }}>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600"><Megaphone className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-navy">{c.name}</p>
                          <p className="text-[11px] text-ink/50">{c.leads} lead{c.leads === 1 ? "" : "s"}</p>
                        </div>
                      </Row>
                    ))}
                  </div>
                )}
                {res.team.length > 0 && (
                  <div className="mb-1">
                    <p className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink/40">Team</p>
                    {res.team.map((t) => (
                      <Row key={t.id} it={{ kind: "team", id: t.id, data: t }}>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-loop/10 text-loop text-[11px] font-bold">{initials(t.name)}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-navy">{t.name}</p>
                          <p className="truncate text-[11px] text-ink/50">{t.role}{t.status && t.status !== "Active" ? ` · ${t.status}` : ""} · {t.email}</p>
                        </div>
                        <UserRound className="h-3.5 w-3.5 text-ink/30" />
                      </Row>
                    ))}
                  </div>
                )}
                {!hasResults && !loading && (
                  <p data-testid="gs-empty" className="px-3 py-6 text-center text-sm text-ink/50">No matches for “{q.trim()}”</p>
                )}
                {q.trim().length >= 2 && (
                  <Row it={{ kind: "all", id: "__all__" }}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-mist text-ink/50"><Search className="h-4 w-4" /></span>
                    <span className="flex-1 text-sm font-medium text-loop">Search all leads for “{q.trim()}”</span>
                    <CornerDownLeft className="h-3.5 w-3.5 text-ink/30" />
                  </Row>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
